import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Stack, Text, Textarea } from '@mantine/core';
import { IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import { useRidesBookingsService, RIDES_BOOKING_STATUS, type RidesBookingView } from '@actschurch/ccms-sdk';
import { ridesKeys, RIDE_DAY_POLL_MS } from '@/lib/queryKeys';
import { campusById, useRidesLookups } from '@/lib/lookups';
import { combineDateTime, countdown, formatShortDate, formatStamp, parseApiDate } from '@/lib/format';
import { isNotFoundError } from '@/lib/copy';
import { usePushCards } from '@/hooks/usePushCards';
import { photoRules, supportLink, useCopy, visiblePhoto } from '@/lib/settings';
import { EMPTY_RIDER, useRidesWizard } from '@/store/ridesWizard';
import { BackLink } from '@/components/rides/PageIntro';
import { PushCardStack } from '@/components/rides/PushCard';
import { ErrorAlert } from '@/components/rides/ErrorAlert';
import { SectionCard } from '@/components/ui/SectionCard';
import { Callout } from '@/components/ui/Callout';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { ConfirmSheet } from '@/components/flows/ConfirmSheet';
import { ReportProblemSheet } from '@/components/flows/ReportProblemSheet';
import { stampClock } from '@/components/flows/LiftCard';
import { RideTicket } from '@/components/flows/RideTicket';
import { bookingWhoLabel, sameTripSelection } from './_shared';
import '@/styles/flows.css';

type Phase = 'waiting' | 'arrived' | 'driverCancelled' | 'youCancelled' | 'completed' | 'noShow';

function phaseOf(b: RidesBookingView): Phase {
  if (b.statusID === RIDES_BOOKING_STATUS.CancelledByDriver) return 'driverCancelled';
  if (b.statusID === RIDES_BOOKING_STATUS.CancelledByRider) return 'youCancelled';
  if (b.statusID === RIDES_BOOKING_STATUS.Completed) return 'completed';
  if (b.statusID === RIDES_BOOKING_STATUS.NoShow) return 'noShow';
  if (b.driverArrivedAt) return 'arrived';
  return 'waiting';
}

/**
 * Ride day for a rider, status first: when the lift leaves (or that the driver is here), the car and
 * plate to look for, where to meet, live updates, then help ("Report a problem") and a quiet
 * "Cancel my seat". A cancelled lift leads with what happened and "Find another lift" for the same trip.
 */
export default function RideDayPage() {
  const { bookingId } = useParams();
  const id = Number(bookingId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const bookings = useRidesBookingsService();
  const lookups = useRidesLookups();
  const setRider = useRidesWizard((s) => s.setRider);
  const { items, push } = usePushCards({ bookingId: id });
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reported, setReported] = useState(false);
  const [reason, setReason] = useState('');
  const { t, settings } = useCopy();
  const photos = photoRules(settings);
  const support = supportLink(settings);

  const q = useQuery({
    queryKey: ridesKeys.bookings.byId(id),
    queryFn: () => bookings.getById(id),
    enabled: Number.isFinite(id) && id > 0,
    refetchInterval: RIDE_DAY_POLL_MS,
  });
  const b = q.data;

  // Status-change cards from polling (driver arrived / cancelled): the fallback when push is off.
  const prev = useRef<RidesBookingView | null>(null);
  useEffect(() => {
    if (!b) return;
    const p = prev.current;
    if (p) {
      if (!p.driverArrivedAt && b.driverArrivedAt) push('Your driver is here', `${b.driverName} is at ${b.collectionName}. Check the number plate: ${b.vehicle.plate}.`, 'ok');
      if (p.statusID !== b.statusID && b.statusID === RIDES_BOOKING_STATUS.CancelledByDriver) push('Your driver cancelled', `${b.driverName} can't make this lift any more. You can look for another lift below.`, 'bad');
    }
    prev.current = b;
  }, [b, push]);

  const cancel = useMutation({
    mutationFn: () => bookings.cancel(id, { reason: reason.trim() || null }),
    onSuccess: () => {
      setCancelOpen(false);
      qc.invalidateQueries({ queryKey: ridesKeys.bookings.all });
      qc.invalidateQueries({ queryKey: ridesKeys.me.summary });
    },
  });

  const when = b ? combineDateTime(b.date, b.departureTime ?? b.serviceTimeStart) : null;
  // Rider cancellation cutoff (settings.riderCancellationCutoffMinutes, 0 = none).
  const cutoff = Math.max(0, settings.riderCancellationCutoffMinutes || 0);
  const insideCutoff = cutoff > 0 && !!when && when.getTime() - Date.now() < cutoff * 60_000;
  const cutoffText = cutoff >= 60 && cutoff % 60 === 0 ? `${cutoff / 60} hour${cutoff === 60 ? '' : 's'}` : `${cutoff} minutes`;
  const pickup = campusById(lookups.data, b?.collectionLocationID);

  const findAnother = () => {
    if (!b) return;
    setRider({ ...EMPTY_RIDER, ...sameTripSelection(b, lookups.data) });
    navigate('/ride/matches');
  };

  if (q.isLoading) {
    return (
      <div className="rides-main rides-flow">
        <BackLink to="/home" label="Home" />
        <LoadingState label="Loading your lift" count={2} />
      </div>
    );
  }
  if (q.error || !b) {
    const gone = isNotFoundError(q.error);
    return (
      <div className="rides-main rides-flow">
        <BackLink to="/home" label="Home" />
        <ErrorState
          title={gone ? "This lift isn't available any more" : "We couldn't load your lift"}
          message={gone ? 'It may have been cancelled or removed. Your rides are on Home.' : undefined}
          error={q.error}
          onRetry={gone ? undefined : () => q.refetch()}
          retrying={q.isFetching}
          secondaryAction={<Button component={Link} to="/home" variant="default" fullWidth>Back to Home</Button>}
        />
      </div>
    );
  }

  const phase = phaseOf(b);
  const driverPhoto = visiblePhoto(b.driverPhotoUrl, photos.driverToRiders);
  const dayWord = (() => {
    const d = parseApiDate(b.date);
    if (!d) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((d.getTime() - today.getTime()) / 86_400_000);
    return diff === 0 ? 'today' : diff === 1 ? 'tomorrow' : `on ${formatShortDate(d)}`;
  })();
  const live = phase === 'waiting' || phase === 'arrived';
  const title = {
    waiting: dayWord ? `Your lift ${dayWord}` : 'Your lift',
    arrived: 'Your driver is here',
    driverCancelled: 'Your driver cancelled',
    youCancelled: 'You cancelled this seat',
    completed: 'This lift is over',
    noShow: 'Marked as a no-show',
  }[phase];
  const soon = live ? countdown(when) : '';
  const statusWord = {
    waiting: soon ? `${soon.charAt(0).toUpperCase()}${soon.slice(1)}` : 'Confirmed',
    arrived: 'Driver is here',
    driverCancelled: 'Cancelled',
    youCancelled: 'Cancelled',
    completed: 'Done',
    noShow: 'No-show',
  }[phase];

  return (
    <div className="rides-main rides-flow">
      <header className="rides-stephead rides-edithead">
        <BackLink to="/home" label="Home" />
        <h1 className="rides-title">{title}</h1>
        <p className="rides-lede">{phase === 'arrived' ? t('rideday.driver_here', undefined, { driver: b.driverName.split(' ')[0], campus: b.collectionName }) : `${b.eventName}, ${formatShortDate(b.date)}.`}</p>
      </header>

      <div className="rides-flow-stack">
        {phase === 'driverCancelled' && (
          <>
            <Callout tone="bad" live>
              {t('rideday.cancelled_by_driver')}
              {b.cancelReason && <span style={{ display: 'block', marginTop: 6 }}>Your driver said: “{b.cancelReason}”</span>}
            </Callout>
            <Button fullWidth onClick={findAnother}>Find another lift</Button>
          </>
        )}
        {phase === 'youCancelled' && <Callout tone="info" live>{t('rideday.cancelled_by_you')}</Callout>}
        {phase === 'completed' && <Callout tone="ok">{t('rideday.completed')}</Callout>}
        {phase === 'noShow' && <Callout tone="warn">{t('rideday.no_show')}</Callout>}

        <RideTicket
          tone={live ? (phase === 'arrived' ? 'ok' : 'live') : 'muted'}
          status={statusWord}
          eventName={b.eventName}
          date={b.date}
          departureTime={b.departureTime}
          serviceTimeStart={b.serviceTimeStart}
          collectionName={b.collectionName}
          dropOffName={b.dropOffName}
          driver={{ name: b.driverName, photoUrl: driverPhoto }}
          vehicle={b.vehicle}
          bigPlate={live}
          meet={live ? { campus: pickup, campusName: b.collectionName } : null}
          rows={[
            { k: "Who's travelling", v: bookingWhoLabel(b) },
            ...(phase === 'arrived' && b.driverArrivedAt ? [{ k: 'Driver arrived', v: stampClock(b.driverArrivedAt) }] : []),
            ...(b.cancelledAt ? [{ k: 'Cancelled', v: formatStamp(b.cancelledAt) }] : []),
          ]}
          bookingRef={b.bookingRef}
        />

        {live && (
          <>
            <Callout tone="info">{t('rideday.instructions')}</Callout>
            {(items.length > 0 || phase === 'waiting') && (
              <section aria-live="polite">
                <h2 className="rides-sectlabel" style={{ marginTop: 4 }}>Updates</h2>
                {items.length ? <PushCardStack items={items} /> : <p className="rides-help">{t('rideday.waiting')}</p>}
              </section>
            )}
          </>
        )}

        <SectionCard title="Need help?" description={t('rideday.help')}>
          {reported && <div style={{ marginBottom: 12 }}><Callout tone="ok" live icon={<IconCircleCheck size={20} />}>{t('report.sent')}</Callout></div>}
          <Stack gap="xs">
            <Button variant="default" fullWidth leftSection={<IconAlertCircle size={20} aria-hidden="true" />} onClick={() => setReportOpen(true)}>Report a problem</Button>
            {support && <Button variant="subtle" fullWidth component="a" href={support.href}>Contact the Carpool team</Button>}
          </Stack>
        </SectionCard>

        {phase !== 'driverCancelled' && !live && (
          <Button variant="default" fullWidth component={Link} to="/ride/event">Book another lift</Button>
        )}

        {live && (
          <div style={{ textAlign: 'center' }}>
            <button type="button" className="rides-linkbtn danger" disabled={insideCutoff} onClick={() => setCancelOpen(true)} style={insideCutoff ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}>
              Cancel my seat
            </button>
            {insideCutoff && <p className="rides-help" style={{ marginTop: 4 }}>{t('rideday.cancel_cutoff', undefined, { cutoff: cutoffText })}</p>}
          </div>
        )}
      </div>

      <ConfirmSheet opened={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel your seat?">
        <Stack gap="md">
          <Text>{t('rideday.cancel_help')}</Text>
          <Textarea label="Why are you cancelling?" description="Optional. Your driver will see this." placeholder="For example, I got a lift with family" value={reason} onChange={(e) => setReason(e.currentTarget.value)} autosize minRows={2} maxLength={300} />
          <ErrorAlert error={cancel.error} title="We couldn't cancel your seat" />
          <Button color="red" fullWidth loading={cancel.isPending} onClick={() => cancel.mutate()}>Yes, cancel my seat</Button>
          <Button variant="default" fullWidth onClick={() => setCancelOpen(false)}>Keep my seat</Button>
        </Stack>
      </ConfirmSheet>

      <ReportProblemSheet opened={reportOpen} onClose={() => setReportOpen(false)} bookingId={b.rideBookingID} onSent={() => setReported(true)} />
    </div>
  );
}
